import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Skeleton } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { eventsApi } from '@/features/events/api';
import { HelpHint } from '@/features/help/HelpHint';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuthStore } from '@/lib/auth/authStore';
import { useJourneyPreview } from '@/components/layout/journeyPreviewStore';
import { useIsBelowDesktop } from '@/lib/hooks/useBreakpoint';
import {
  despliegueDeEstacion,
  esEnlaceDeXotski,
  xurveyApi,
  xurveyPublicUrl,
  type DeploymentOverrides,
  type XurveyDeployment,
  type PosterPreset,
} from '@/lib/api/xurvey';
import type { XkStation } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { DesignPane } from './DesignPane';
import { TestQrDialog } from './TestQrDialog';
import { StylePresetBar } from './StylePresetBar';
import { usePreviewFrame } from './previewChannel';
import { StepsPane } from './StepsPane';
import { ConsentCard } from './ConsentCard';
import { JourneyTopControls } from './JourneyTopControls';
import { ScreenControlCard } from './ScreenControlCard';
import { XurveyDeploySection } from '../xurvey/XurveyDeploySection';
import type { TokenColor } from './tokens';
import {
  AUTOGUARDADO_MS,
  agruparPorEvento,
  cortaAutoguardado,
  cortaGuardadoExplicito,
  escribirEnCadaUno,
  esFallo,
  esperaTrasLimite,
  esRechazo,
  motivoDeError,
  type ResultadoEscritura,
} from './resultadoDeGuardado';

/**
 * Design Journey — cómo se ve la encuesta en ESTE evento.
 *
 * Tres paneles, y es la forma que van a heredar Xamplin y DiD·ox:
 *
 * ```
 *   pasos          │      móvil        │   herramientas
 *   reordenar      │   el enlace REAL  │   apariencia
 *   ocultar        │   con los datos   │   estilo
 *   ir a uno       │   de este evento  │   form
 * ```
 *
 * **El móvil no es una maqueta**: es el enlace público del despliegue dentro
 * de un iframe, en modo vista previa. Xotski tenía 1.481 líneas imitándolo y
 * el resultado fue el previsible — la imitación divergió y era justo la que se
 * miraba para decidir si algo estaba bien. Lo que hace falta para dirigirlo
 * —«enséñame el paso 3», «píntalo así»— viaja por el canal
 * (`previewChannel.ts`), no reimplementando el renderer.
 *
 * **Se edita el despliegue, nunca la encuesta.** El mismo diseño puede estar
 * vivo en treinta eventos; aquí se pinta uno sin tocarle el trabajo a los
 * demás. Por eso tampoco hay estructura: ni añadir pasos, ni componentes, ni
 * etiquetas (protocolo §3).
 *
 * Se guarda a mano y no al vuelo: el panel manda cada cambio al móvil al
 * instante, pero escribir en el servidor cada vez que alguien arrastra un
 * deslizador dejaría el evento en un estado intermedio que nadie eligió.
 */
/**
 * El alcance: un evento, o todos los de un proyecto.
 *
 * Union y no dos props opcionales sueltas para que no se pueda escribir
 * `<DesignJourneySection />` sin ninguno de los dos — que compilaría y luego
 * enseñaría una pantalla vacía sin decir por qué.
 */
/**
 * Una ZONA de la pantalla: superficie propia, separada por AIRE.
 *
 * Antes las cuatro zonas —cabecera, pasos, previa y referencia— se separaban
 * con filos de 1 px (`border-r`, `border-l`, `border-t`). Tres problemas:
 * un filo entre dos fondos idénticos no separa nada, sólo raya; en oscuro se
 * perdía del todo; y al envolver las columnas en tableta los filos quedaban
 * colgando en mitad de la nada, dividiendo lo que ya no estaba al lado.
 *
 * Lo que separa aquí es el hueco y un escalón de superficie. `bg-elevated` está
 * un peldaño por encima del `bg-surface` de la tarjeta en los dos temas, así
 * que cada zona se lee como una pieza suelta sin dibujar una sola línea.
 */
const ZONA =
  'rounded-xl bg-[var(--color-bg-elevated)] p-4 shadow-[var(--shadow-card)]';

/*
 * `eventNames` sólo existe con `eventIds`: desde un proyecto, el informe de un
 * guardado parcial dice QUÉ eventos no se guardaron, y un id no le dice nada a
 * quien diseña. Desde un evento no hace falta — sólo hay uno.
 */
export type DesignJourneyScope =
  | { eventId: number; eventIds?: never; eventNames?: never }
  | { eventIds: number[]; eventId?: never; eventNames?: Record<number, string> };

/**
 * Lo que se manda al guardar: el borrador entero de un despliegue.
 *
 * `ov` se compara por REFERENCIA para saber si el borrador cambió: todos los
 * manejadores de edición crean un objeto nuevo con `setOv`, así que «es el
 * mismo objeto» quiere decir «nadie ha tocado nada desde entonces».
 */
type Borrador = { ov: DeploymentOverrides; game: string; defaultLocale: string };

/*
 * El cuerpo del PATCH, idéntico al de siempre. Vacío = «el de la plantilla»:
 * se manda `null` explícito porque `undefined` dejaría el juego anterior puesto
 * y parecería que guardar no ha hecho nada.
 */
function cuerpoDe(b: Borrador) {
  return {
    overrides: b.ov,
    gameCode: b.game === '' ? null : b.game,
    defaultLocale: b.defaultLocale === '' ? null : b.defaultLocale,
  };
}

export function DesignJourneySection({ eventId, eventIds, eventNames }: DesignJourneyScope) {
  const t = useT();
  const qc = useQueryClient();

  /*
   * A qué eventos alcanza esta pantalla.
   *
   * Uno cuando se abre desde un evento; todos los del proyecto cuando se abre
   * desde su pestaña. Es la ÚNICA diferencia entre los dos casos: el resto
   * —paneles, previa, guardado— es idéntico, porque «diseñar una vez y aplicar
   * a lo que haya debajo» es la misma operación con distinto alcance.
   *
   * Duplicar la pantalla para el proyecto habría sido copiar quinientas líneas
   * que luego divergen: la de eventos gana una opción, la de proyectos no, y en
   * seis meses el mismo botón hace cosas distintas según por dónde entres.
   */
  const ids = useMemo(
    () => (eventIds && eventIds.length ? eventIds : eventId != null ? [eventId] : []),
    [eventId, eventIds],
  );

  const stationsQ = useQuery({
    queryKey: ['stations', 'scope', ids.join(',')],
    queryFn: async () => {
      const listas = await Promise.all(ids.map((id) => eventsApi.listStations({ eventId: id })));
      // Se conserva de qué evento es cada estación: sin eso no se puede decir
      // a cuántos eventos alcanza de verdad lo que se está diseñando.
      return {
        items: listas.flatMap((l, i) =>
          l.items.map((st) => ({ ...st, eventId: st.eventId ?? ids[i]! })),
        ),
      };
    },
    enabled: ids.length > 0,
  });
  // Memorizada: de ella cuelga el `useMemo` de los despliegues, y un array
  // nuevo en cada render lo recalcularía siempre — que es lo mismo que no
  // tenerlo.
  const estaciones = useMemo(
    () => (stationsQ.data?.items ?? []) as XkStation[],
    [stationsQ.data?.items],
  );
  // La primera con encuesta manda: todas las del evento corren la misma y el
  // diseño se escribe en todas por igual.
  const station = estaciones.find((s) => s.surveysId);

  const encuestasQ = useQuery({
    queryKey: ['xurvey', 'surveys', 'station'],
    queryFn: () => xurveyApi.listSurveys({ scope: 'station' }),
    staleTime: 60_000,
  });

  /*
   * Qué encuesta se está mirando.
   *
   * El estado vive AQUÍ y no en el selector porque de él cuelgan los pasos, el
   * móvil y los overrides. Con el estado dentro del selector, cambiar de
   * encuesta movía el desplegable y dejaba el simulador enseñando la anterior:
   * el editor decía una cosa y la previa otra.
   *
   * Sin elección explícita manda la que corre el evento; si ninguna, la
   * primera publicada, para abrir enseñando algo en vez de un hueco.
   */
  const [elegida, setElegida] = useState<number | null>(null);
  const surveyId = elegida ?? station?.surveysId ?? encuestasQ.data?.items[0]?.id ?? null;

  const deploymentsQ = useQuery({
    queryKey: ['xurvey', 'deployments', surveyId],
    queryFn: () => xurveyApi.listDeployments(surveyId!),
    enabled: !!surveyId,
    staleTime: 60_000,
  });
  const despliegues = useMemo(
    () =>
      (deploymentsQ.data ?? []).filter(
        (d) =>
          // Los QR de PRUEBA (F2b) no cuentan como despliegue del diseño: sin
          // este filtro el borrador podía sembrarse de un enlace de ensayo y
          // el «se guarda en N enlaces» mentía.
          !d.isTest &&
          estaciones.some((s) => String(s.id) === String(d.entityId)),
      ),
    [deploymentsQ.data, estaciones],
  );

  /** El QR de prueba vivo de esta encuesta en este evento, si hay. */
  const enlaceDePrueba = useMemo(
    () =>
      (deploymentsQ.data ?? []).find(
        (d) =>
          d.isTest &&
          d.isActive &&
          estaciones.some((s) => String(s.id) === String(d.entityId)),
      ) ?? null,
    [deploymentsQ.data, estaciones],
  );
  /*
   * Dónde se ESCRIBE: los reales y, si lo hay, el QR de prueba vivo.
   *
   * El de prueba no cuenta como despliegue del diseño —no siembra el borrador
   * ni suma en «se guarda en N enlaces»— pero tiene que recibir lo que se
   * guarda: es el QR que se escanea mientras se diseña, y sin esto enseñaba
   * la plantilla desnuda. «Cambio el fondo, guardo, y el QR no lo tiene» era
   * exactamente eso.
   */
  const objetivos = useMemo(
    () => (enlaceDePrueba ? [...despliegues, enlaceDePrueba] : despliegues),
    [despliegues, enlaceDePrueba],
  );
  /*
   * El despliegue que se previsualiza.
   *
   * El de la estación de referencia si la encuesta elegida está puesta ahí;
   * si no, el primero que tenga. Cambiar de encuesta en el selector puede
   * dejar la estación de referencia sin despliegue de la nueva, y enseñar
   * entonces el de la encuesta anterior sería mentir sobre lo que se está
   * editando.
   */
  /*
   * Y fijado mientras se edita: si un refetch reordena la lista, el borrador
   * NO cambia de despliegue por debajo (resembraría `ov` y pisaría lo que se
   * estaba tocando). Al cambiar de encuesta el id ya no está y se vuelve a
   * elegir.
   */
  const baseFijado = useRef<number | null>(null);
  const base: XurveyDeployment | undefined =
    despliegues.find((d) => d.id === baseFijado.current) ??
    // El activo de la estación de referencia: el mismo que lleva su QR. Con
    // un cerrado de antes delante, la previa enseñaba un enlace que nadie
    // escanea.
    despliegueDeEstacion(despliegues, station?.id) ??
    despliegues[0];

  /** Cuántos eventos distintos tienen desplegada la encuesta que se edita. */
  const eventosAlcanzados = useMemo(() => {
    const porEstacion = new Map(estaciones.map((s) => [String(s.id), s.eventId]));
    return new Set(
      despliegues.map((d) => porEstacion.get(String(d.entityId))).filter((x) => x != null),
    ).size;
  }, [despliegues, estaciones]);

  const encuesta = encuestasQ.data?.items.find((s) => s.id === surveyId) ?? null;

  /*
   * La encuesta desplegada, pedida por id.
   *
   * El listado de arriba sólo trae las PUBLICADAS —es el que sirve para elegir
   * qué desplegar— y aquí no se elige: se edita un despliegue que ya existe.
   * Si su encuesta está archivada, `encuesta` sale null y con ella los idiomas,
   * así que el selector desaparecía sin decir por qué. Medido con la 110.
   */
  const encuestaQ = useQuery({
    queryKey: ['xurvey', 'survey', surveyId],
    queryFn: () => xurveyApi.getSurvey(surveyId!),
    enabled: surveyId != null,
    staleTime: 60_000,
  });
  const idiomasEncuesta = encuestaQ.data?.languages ?? encuesta?.languages ?? [];

  /*
   * ¿Enlace de Xotski? Decide cuánto enseña la pestaña Consentimiento. Mientras
   * no se sabe (cargando) es `undefined` y la tarjeta espera; si la encuesta no
   * se puede leer, `false`: la vista de siempre, nunca se oculta a ciegas.
   */
  const esXotski: boolean | undefined = !base
    ? undefined
    : encuestaQ.isSuccess
      ? esEnlaceDeXotski({ app: base.app, productType: encuestaQ.data.productType })
      : encuestaQ.isError
        ? false
        : undefined;

  /*
   * Quién termina y dónde se cae.
   *
   * Se pide aquí, en la pantalla donde se diseña, y no en una de métricas
   * aparte: la pregunta que responde es «¿qué arreglo?», y se contesta mirando
   * el paso en el que la gente se va — no una tabla en otro sitio.
   */
  const statsQ = useQuery({
    queryKey: ['xurvey', 'stats', surveyId],
    queryFn: () => xurveyApi.surveyStats(surveyId!),
    enabled: !!surveyId,
    staleTime: 30_000,
  });
  const abandonos = useMemo(() => {
    const m: Record<number, number> = {};
    for (const d of statsQ.data?.dropOff ?? []) m[d.stepId] = d.abandoned;
    return m;
  }, [statsQ.data?.dropOff]);

  // ── El borrador ───────────────────────────────────────────────────
  const [ov, setOv] = useState<DeploymentOverrides>({});
  // El juego NO va en `overrides`: es columna del despliegue, porque el runtime
  // lo necesita para servir el enlace sin abrir el diseño entero.
  const [game, setGame] = useState('');
  // El idioma con el que ARRANCA este despliegue. Columna, como `gameCode`.
  const [defaultLocale, setDefaultLocale] = useState('');
  // Si se tocó la apariencia después de aplicar un estilo guardado.
  const [presetTocado, setPresetTocado] = useState(false);
  // Lo mismo para el cartel: si se tocó después de aplicar un diseño guardado.
  const [posterPresetTocado, setPosterPresetTocado] = useState(false);
  const [sucio, setSucio] = useState(false);
  /** Cuándo se guardó por última vez. `null` = todavía nada en esta sesión. */
  const [guardadoEn, setGuardadoEn] = useState<Date | null>(null);
  /*
   * El último borrador que el servidor RECHAZÓ (un 4xx que no es 429), con el
   * porqué ya traducido.
   *
   * Mientras el borrador siga siendo ése, ni el autoguardado ni el guardado al
   * salir lo vuelven a mandar: la respuesta sería la misma. Cualquier edición
   * crea otro borrador y la pausa se levanta sola, sin que haya que acordarse
   * de limpiarla en cada uno de los diez manejadores de edición.
   */
  const [rechazo, setRechazo] = useState<(Borrador & { motivo: string }) | null>(null);
  // Espera tras un 429: del dispositivo, no del borrador (no se toca al sembrar).
  const [espera, setEspera] = useState<{ hasta: number } | null>(null);
  const enPausa =
    rechazo !== null &&
    rechazo.ov === ov &&
    rechazo.game === game &&
    rechazo.defaultLocale === defaultLocale;
  // El porqué bajo «Guardar»: el rechazo en pausa o, si no, el 429 en espera.
  const motivoVisible = enPausa && rechazo
    ? rechazo.motivo
    : espera
      ? t('designJourney.saveError.rateLimited')
      : null;
  /*
   * De qué despliegue se sembró el borrador.
   *
   * Se siembra UNA vez por despliegue, no cada vez que la consulta devuelve
   * datos. `overrides` es un objeto nuevo en cada respuesta, así que dependiendo
   * de él —como estaba— cualquier refresco de la consulta borraba lo que se
   * estuviera editando y apagaba «Guardar». Desde fuera parecía que los
   * cambios sólo cuajaban al desplegar, cuando lo que pasaba es que se perdían
   * antes de poder guardarlos.
   */
  const sembradoDe = useRef<number | null>(null);
  useEffect(() => {
    if (!base || sembradoDe.current === base.id) return;
    sembradoDe.current = base.id;
    baseFijado.current = base.id;
    setOv(base.overrides ?? {});
    setGame(base.gameCode ?? '');
    setDefaultLocale(base.defaultLocale ?? '');
    setPresetTocado(false);
    setPosterPresetTocado(false);
    setSucio(false);
    // Otro despliegue, otro borrador: lo que se rechazó era del anterior.
    setRechazo(null);
  }, [base]);

  // ── El canal con el móvil ─────────────────────────────────────────
  const origen = useMemo(() => new URL(xurveyPublicUrl('x')).origin, []);
  const canal = usePreviewFrame(origen, base?.slug ?? null);

  /*
   * ── La capa legal de este despliegue ───────────────────────────────
   *
   * Lo deriva el backend (`consent-config`): qué instrumentos verá el
   * invitado, qué pasos gobierna el sistema (candado) y qué impediría activar.
   * Antes aquí se miraba a mano si la marca tenía `termsText`; ahora la fuente
   * es una sola —la misma que sirve el bundle y escribe el registro— y esta
   * pantalla sólo la enseña. Se pide del despliegue previsualizado y se
   * refresca al guardar, porque fotos/menores viven en `overrides.consent`.
   */
  const consentQ = useQuery({
    queryKey: queryKeys.xurvey.consentConfig(base?.id ?? 0),
    queryFn: () => xurveyApi.consentConfig(base!.id),
    enabled: !!base,
    staleTime: 30_000,
  });
  const pasosLegales = useMemo(() => new Set(consentQ.data?.legalStepIds ?? []), [consentQ.data?.legalStepIds]);
  /*
   * Los pasos, marcados como legales cuando lo dice la capa legal O cuando la
   * previa declara un consentimiento/puerta de edad dentro: así el candado
   * sale aunque la consulta todavía no haya vuelto.
   */
  const pasosConCandado = useMemo(
    () =>
      canal.pasos.map((p) => ({
        ...p,
        legal:
          pasosLegales.has(p.id) ||
          (p.components ?? []).some((c) => c.type === 'consentForm' || c.type === 'ageGate'),
      })),
    [canal.pasos, pasosLegales],
  );

  const [pasoSel, setPasoSel] = useState<number | null>(null);

  /*
   * El estado guardado, una vez, cuando la previa dice que ya escucha.
   *
   * Los cambios sueltos NO pasan por aquí: los manda `patch`, y sólo la parte
   * que se toca. Mandarlo todo en cada tecla rehacía la lista de pasos dentro
   * del runtime —arrays nuevos, aunque su contenido fuera el mismo— y eso le
   * hacía volver a pedir las opciones de cada componente enlazado. De ahí el
   * retardo al arrastrar un color: no era la pintura, era el trabajo de más.
   */
  useEffect(() => {
    if (!canal.listo) return;
    canal.enviar({
      type: 'xurvey:preview/overrides',
      design: ov.design ?? {},
      styleOverrides: ov.styleOverrides ?? {},
      hiddenStepIds: ov.hiddenStepIds ?? [],
      stepOrder: ov.stepOrder ?? [],
    });
    // A propósito sólo cuando la previa se declara lista: `ov` aquí sería
    // volver al envío completo por cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canal.listo, base?.slug]);

  // El paso que se elige a la izquierda es el que se ve en el móvil.
  useEffect(() => {
    if (pasoSel != null) canal.enviar({ type: 'xurvey:preview/goToStep', stepId: pasoSel });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasoSel, canal.enviar]);

  // Y al revés: quien navega dentro del móvil mueve la selección.
  useEffect(() => {
    if (canal.pasoActual != null) setPasoSel(canal.pasoActual);
  }, [canal.pasoActual]);

  // Al cambiar de encuesta, el paso elegido apuntaba a uno de la anterior: un
  // id que en la nueva o no existe o es otro paso distinto.
  useEffect(() => {
    setPasoSel(null);
  }, [base?.slug]);

  // ── El rail derecho ───────────────────────────────────────────────
  const belowDesktop = useIsBelowDesktop();
  const abrirRail = useJourneyPreview((s) => s.open);
  const cerrarRail = useJourneyPreview((s) => s.close);
  const slot = useJourneyPreview((s) => s.slotEl);
  useEffect(() => {
    if (belowDesktop) return;
    abrirRail();
    return () => cerrarRail();
  }, [belowDesktop, abrirRail, cerrarRail]);

  // ── Guardar ───────────────────────────────────────────────────────
  /** De qué evento es cada destino: las estaciones ya lo traen. */
  const eventoDe = useCallback(
    (d: XurveyDeployment) =>
      estaciones.find((s) => String(s.id) === String(d.entityId))?.eventId ?? null,
    [estaciones],
  );

  /** El nombre con el que se cuenta un evento en el informe. */
  const nombreDeEvento = (id: number | null) => {
    if (id != null) {
      const delProyecto = eventNames?.[id];
      if (delProyecto) return delProyecto;
      const estacion = estaciones.find((s) => s.eventId === id);
      if (estacion?.name) return estacion.name;
    }
    return t('designJourney.saveReport.unnamedEvent');
  };

  /*
   * El aviso de un guardado que no llegó a todos.
   *
   * En un evento se cuentan ENLACES (sus estaciones y su QR de prueba); desde
   * un proyecto, EVENTOS, con el nombre y el porqué de los que se quedaron
   * fuera — tres como mucho, que el toast dura cuatro segundos y es texto
   * plano. Antes el guardado se paraba en el primer fallo y el toast sólo
   * decía el mensaje del servidor: no se sabía qué se había guardado y qué no.
   */
  const informeDeGuardado = (resultados: ResultadoEscritura<XurveyDeployment>[]) => {
    const grupos = agruparPorEvento(resultados, eventoDe);
    if (grupos.length <= 1) {
      const ok = resultados.filter((r) => r.ok).length;
      const primerFallo = resultados.find(esFallo);
      return {
        title:
          ok === 0
            ? t('designJourney.saveReport.noneSaved')
            : t('designJourney.saveReport.someLinks', { ok, total: resultados.length }),
        description: primerFallo ? motivoDeError(primerFallo.error, t) : undefined,
      };
    }
    const fallidos = grupos.filter((g) => g.fallidos > 0);
    const partes = fallidos.slice(0, 3).map((g) =>
      t('designJourney.saveReport.eventReason', {
        name: nombreDeEvento(g.eventId),
        reason: motivoDeError(g.error, t),
      }),
    );
    if (fallidos.length > 3) {
      partes.push(t('designJourney.saveReport.more', { n: fallidos.length - 3 }));
    }
    return {
      title: t('designJourney.saveReport.events', {
        ok: grupos.length - fallidos.length,
        total: grupos.length,
      }),
      description: partes.join(' · '),
    };
  };

  const guardar = useMutation({
    /*
     * A todos los destinos —las estaciones del evento (o del proyecto) y su QR
     * de prueba—: comparten cartelería y marca, y pedir que se pinte cada una
     * por separado sería trabajo repetido para un resultado que nadie quiere
     * distinto. Se manda el borrador que viaja en `vars`, no el del cierre:
     * así se sabe exactamente QUÉ fue lo rechazado.
     *
     * Nunca lanza: devuelve un resultado por destino (ver
     * `resultadoDeGuardado.ts`), y si uno dice que no, los demás se intentan.
     */
    mutationFn: (vars: { aMano?: boolean; borrador: Borrador }) =>
      escribirEnCadaUno(
        objetivos,
        (d) => xurveyApi.updateDeployment(d.id, cuerpoDe(vars.borrador)),
        // El botón recorre todos y cuenta; el autoguardado no insiste (D-6).
        { cortarEn: vars.aMano ? cortaGuardadoExplicito : cortaAutoguardado },
      ),
    onSuccess: (resultados, vars) => {
      if (resultados.some((r) => r.ok)) {
        qc.invalidateQueries({ queryKey: ['xurvey', 'deployments', surveyId] });
        if (base) qc.invalidateQueries({ queryKey: queryKeys.xurvey.consentConfig(base.id) });
      }
      const fallidos = resultados.filter(esFallo);
      if (fallidos.length === 0) {
        setSucio(false);
        setGuardadoEn(new Date());
        setRechazo(null);
        setEspera(null);
        /*
         * El aviso, sólo cuando se pulsa.
         *
         * Un toast por cada autoguardado sería un cartel saltando cada pocos
         * segundos mientras alguien arrastra un color: acabaría tapando la
         * previa y enseñando a ignorar los avisos, que es peor que no tenerlos.
         * El autoguardado se cuenta en la línea de estado de al lado del botón.
         */
        if (vars.aMano) {
          toast({ tone: 'success', title: t('designJourney.saved', { n: String(despliegues.length) }) });
        }
        return;
      }
      /*
       * Algo no se guardó: `sucio` sigue en pie.
       *
       * Si algún destino lo RECHAZÓ, el borrador entero queda en pausa —aunque
       * otro diera un 5xx—: volver a mandarlo chocaría con el mismo 4xx. Si
       * todo fueron fallos pasajeros, no hay pausa y el autoguardado lo repite
       * como siempre.
       *
       * Los fallos sí se avisan aunque sea un autoguardado: es UN toast por
       * borrador, no uno cada 2,5 s, porque con la pausa ya no hay bucle.
       */
      // La espera de un 429 es la MAYOR de la ronda; sin 429, no hay espera.
      const esperas = fallidos
        .filter((r) => r.intentado)
        .map((r) => esperaTrasLimite(r.error))
        .filter((ms): ms is number => ms !== null);
      setEspera(esperas.length > 0 ? { hasta: Date.now() + Math.max(...esperas) } : null);
      const primerRechazo = fallidos.find((r) => esRechazo(r.error));
      setRechazo(
        primerRechazo
          ? { ...vars.borrador, motivo: motivoDeError(primerRechazo.error, t) }
          : null,
      );
      toast({ tone: 'error', ...informeDeGuardado(resultados) });
    },
    // Para lo inesperado: un fallo de programación, no de red ni del servidor,
    // que ésos ya vienen contados en los resultados.
    onError: (e: Error) => toast({ tone: 'error', title: motivoDeError(e, t) }),
  });

  /*
   * Autoguardado.
   *
   * El panel era el único sitio del producto donde lo que se ve NO es lo que
   * hay guardado: la previa obedece al momento y los enlaces sólo cambian al
   * pulsar. Quien lo prueba abriendo el QR en el móvil ve el diseño de antes y
   * lo lee como un fallo del producto, no como un botón sin pulsar.
   *
   * **Dos segundos y medio desde el último cambio**, no en cada uno. Guardar
   * escribe en todos los despliegues del evento —una petición por estación—, y
   * arrastrar un selector de color dispara decenas de cambios por segundo: sin
   * la espera, un evento de tres estaciones haría cientos de escrituras por un
   * color que todavía se estaba eligiendo.
   *
   * No hay red de seguridad al cerrar la pestaña a propósito: con esta espera,
   * lo que se perdería es el último cambio de hace dos segundos, y un aviso de
   * «¿seguro que quieres salir?» en cada visita molesta más de lo que salva.
   *
   * **Y se para tras un rechazo.** Antes, al terminar una petición fallida
   * `isPending` volvía a `false`, `sucio` seguía en pie y este efecto se
   * rearmaba: con un juego y sin Official Rules, un 422 y un toast rojo cada
   * 2,5 s mientras la pantalla estuviera abierta, también por un cambio de
   * color (el bucle de 422 de la épica #333). Ahora, si el servidor dijo que no
   * a ESTE borrador (`enPausa`), no se vuelve a mandar hasta que algo cambie;
   * «Guardar» a mano sí lo intenta siempre. Los fallos pasajeros (red, 429,
   * 5xx) no pausan y se reintentan: ahí esperar sí arregla.
   *
   * **Y no insiste contra un servidor caído** (D-6 de la épica #333). Con N
   * destinos, cada ronda mandaba N PATCH aunque el primero ya hubiera dicho
   * «no puedo»: ahora la ronda del autoguardado corta en el primer fallo
   * pasajero (1 PATCH por ronda). Un 429 espera su `Retry-After` —o 60 s si no
   * viene o el navegador no lo ve— y un 401 corta todo: la sesión ya cayó. El
   * botón «Guardar», que sí tiene a alguien esperando el informe, recorre todos.
   */
  useEffect(() => {
    if (!sucio || guardar.isPending || despliegues.length === 0 || enPausa) return;
    // `id` y no `t`: aquí `t` taparía al traductor.
    const retraso = Math.max(AUTOGUARDADO_MS, (espera?.hasta ?? 0) - Date.now());
    const id = window.setTimeout(
      () => guardar.mutate({ borrador: { ov, game, defaultLocale } }),
      retraso,
    );
    return () => window.clearTimeout(id);
    // `guardar` es estable entre renders salvo por su estado; se mira
    // `isPending` para no encadenar un guardado sobre otro que sigue en vuelo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucio, guardar.isPending, despliegues.length, ov, game, defaultLocale, enPausa, espera]);

  /*
   * Lo pendiente se escribe AL SALIR.
   *
   * El autoguardado espera 2,5 s, y el `clearTimeout` de arriba cancelaba
   * ese guardado al desmontar: ocultar un paso y cambiar de pestaña antes de
   * que pasaran los segundos perdía el cambio — «los pasos ocultos se
   * pierden al salir y volver». Aquí se manda lo último que había, sin
   * esperar: las peticiones sobreviven al componente.
   *
   * Salvo que lo último que había sea justo lo que el servidor RECHAZÓ: se
   * mandaba igual, otro 422 por despliegue tragado en silencio. Si después del
   * rechazo se editó algo, es otro borrador y sí se manda.
   *
   * Va en serie y cortando en el primer fallo pasajero, como el autoguardado:
   * contra un servidor caído, 1 PATCH y no N a la vez. No mira la espera del
   * 429: si el bloqueo ya acabó, salva el cambio; si no, es un 429 más.
   *
   * Tampoco sin sesión: tras un 401 con el refresco fallido, `logout()` borra
   * el token y desmonta la pantalla antes de que quede puesta la pausa, y esto
   * mandaba un segundo PATCH sin `Authorization` (D-7 de XCS-15, CA-14).
   */
  const ultimo = useRef({ ov, game, defaultLocale, sucio, despliegues, objetivos, enPausa });
  ultimo.current = { ov, game, defaultLocale, sucio, despliegues, objetivos, enPausa };
  useEffect(
    () => () => {
      const u = ultimo.current;
      if (!u.sucio || u.despliegues.length === 0 || u.enPausa) return;
      if (!useAuthStore.getState().token) return;
      // Ya no hay pantalla donde avisar: el resultado se descarta, y el
      // siguiente guardado lo repite. Las promesas sobreviven al componente.
      void escribirEnCadaUno(
        u.objetivos,
        (d) =>
          xurveyApi.updateDeployment(
            d.id,
            cuerpoDe({ ov: u.ov, game: u.game, defaultLocale: u.defaultLocale }),
          ),
        { cortarEn: cortaAutoguardado },
      );
    },
    [],
  );

  // Estaciones del evento que todavía no corren ESTA encuesta.
  const sinDesplegar = estaciones.length - despliegues.length;

  /**
   * Cambiar algo: al borrador y, en el mismo gesto, a la previa.
   *
   * Se manda **sólo lo que cambia**, que es lo que el canal pide. La previa lo
   * funde sobre lo que ya tenía, así que arrastrar un color no vuelve a
   * declarar los pasos ocultos ni el orden — y sin arrays nuevos, el runtime no
   * rehace su lista ni vuelve a resolver enlaces de datos.
   */
  const patch = useCallback(
    (delta: Partial<DeploymentOverrides>) => {
      setOv((o) => ({ ...o, ...delta }));
      setSucio(true);
      canal.enviar({ type: 'xurvey:preview/overrides', ...delta });
    },
    [canal.enviar],
  );

  /*
   * Los manejadores, estables.
   *
   * No es adorno: sin ellos, cada tecla en el panel de colores volvía a
   * renderizar la lista de pasos —con su contexto de arrastre— y el bloque de
   * encuesta y enlaces, que no tienen nada que ver con un color. Medido: 132 ms
   * por cambio, y **el móvil no tenía la culpa** (135 con él, 132 sin él).
   *
   * Con esto y `memo` en los tres paneles, sólo se repinta el que cambia.
   */
  const alDesign = useCallback(
    (delta: Record<string, unknown>) => {
      // Primero el móvil: es un `postMessage`, cuesta nada y es lo que se está
      // mirando. Después el borrador, en transición.
      canal.enviar({ type: 'xurvey:preview/overrides', design: delta });
      setSucio(true);
      setPresetTocado(true);
      startTransition(() => {
        // Fusión por dentro también aquí: el borrador debe quedar como lo que
        // ve el móvil, y el canal funde clave a clave.
        setOv((o) => ({ ...o, design: { ...(o.design ?? {}), ...delta } }));
      });
    },
    [canal.enviar],
  );

  /*
   * Un color, en dos tiempos.
   *
   * Al móvil se le manda YA —un `postMessage` no cuesta nada— y el repintado
   * del panel va en transición, que React puede interrumpir si llega otro
   * cambio. Arrastrar el selector dispara decenas de cambios por segundo, y
   * medido cada uno costaba ~30 ms de render síncrono: suficiente para que se
   * note el tirón justo en el gesto donde más molesta.
   *
   * El campo no se queda atrás porque mantiene su propio eco (ver `FilaColor`).
   */
  const alToken = useCallback(
    (key: TokenColor, value: string | null) => {
      canal.enviar({ type: 'xurvey:preview/overrides', styleOverrides: { [key]: value ?? '' } });
      setSucio(true);
      setPresetTocado(true);
      startTransition(() => {
      setOv((o) => {
        const siguiente = { ...(o.styleOverrides ?? {}) };
        // `null` quita el override y devuelve el color del tema. Guardar una
        // cadena vacía dejaría el componente pintado de nada.
        if (value == null) delete siguiente[key];
        else siguiente[key] = value;
        return { ...o, styleOverrides: siguiente };
      });
      });
    },
    [canal.enviar],
  );

  /**
   * La apariencia tal como se está viendo, para poder guardarla.
   *
   * Lo EFECTIVO —lo que trae la encuesta más lo que este despliegue pisa—, no
   * sólo lo pisado. Es la misma razón por la que un theme de empresa guarda la
   * paleta entera: un estilo que sólo llevara el diff se vería distinto en cada
   * encuesta a la que se aplicara, y lo que se espera al elegir «Verano 2026»
   * es ver Verano 2026.
   */
  const apariencia = useMemo(
    () => ({
      design: { ...canal.heredado.design, ...(ov.design ?? {}) },
      styleOverrides: { ...canal.heredado.tokens, ...(ov.styleOverrides ?? {}) },
    }),
    [canal.heredado, ov.design, ov.styleOverrides],
  );

  /**
   * Aplicar un estilo guardado: va entero, en un solo mensaje.
   *
   * Se manda todo de golpe y no clave a clave porque son decenas y cada una
   * repintaría la previa; y como el canal funde por dentro, lo que el estilo no
   * traiga se queda como estaba.
   */
  const aplicarEstilo = useCallback(
    (estilo: { id: number; design: Record<string, unknown> | null; styleOverrides: Record<string, string> | null }) => {
      const design = estilo.design ?? {};
      const tokens = estilo.styleOverrides ?? {};
      canal.enviar({ type: 'xurvey:preview/overrides', design, styleOverrides: tokens });
      setSucio(true);
      setPresetTocado(false);
      setOv((o) => ({
        ...o,
        // La IDENTIDAD del estilo se guarda con el despliegue: al salir y
        // volver, el selector sigue marcando de dónde se partió.
        stylePresetId: estilo.id,
        design: { ...(o.design ?? {}), ...design },
        styleOverrides: { ...(o.styleOverrides ?? {}), ...tokens },
      }));
    },
    [canal.enviar],
  );

  /** La imagen de la bienvenida de este evento. Se funde clave a clave. */
  const alWelcome = useCallback(
    (delta: { imageUrl?: string; imageRole?: 'hero' | 'logo' | 'background' }) => {
      canal.enviar({ type: 'xurvey:preview/overrides', welcome: delta });
      setSucio(true);
      setOv((o) => ({ ...o, welcome: { ...(o.welcome ?? {}), ...delta } }));
    },
    [canal.enviar],
  );

  /**
   * Con qué idioma arranca. Es columna del despliegue, como el juego, así que
   * no pasa por `patch()`; a la previa se le pide que se mire en ese idioma.
   */
  const alDefaultLocale = useCallback(
    (l: string) => {
      setDefaultLocale(l);
      setSucio(true);
      const ver = l || encuestaQ.data?.defaultLanguage || idiomasEncuesta[0];
      if (ver) canal.enviar({ type: 'xurvey:preview/overrides', locale: ver });
    },
    [canal.enviar, encuestaQ.data?.defaultLanguage, idiomasEncuesta],
  );

  const encuestaConJuego = canal.pasos.some((p) => p.hasGame);
  const encuestaConWelcome = canal.pasos.some((p) =>
    p.components.some((c) => c.type === 'welcomeScreen'),
  );

  /**
   * El cartel de este despliegue.
   *
   * Se funde clave a clave como el diseño: el panel manda sólo lo que toca, y
   * lo que no traiga se queda como esté en la encuesta. Al cartel embebido se
   * lo manda `PosterPane` por su cuenta —tiene su propio iframe— así que aquí
   * sólo se guarda en el borrador.
   */
  const alPoster = useCallback(
    (delta: Record<string, unknown>) => {
      setSucio(true);
      setPosterPresetTocado(true);
      setOv((o) => ({ ...o, poster: { ...(o.poster ?? {}), ...delta } }));
    },
    [],
  );

  /**
   * Aplicar un diseño de cartel guardado. Sólo el diseño —fondo, colores,
   * QR— encima del cartel actual: el titular y los textos de ESTE evento se
   * quedan. Al cartel embebido ya se lo mandó `PosterPane`; aquí se guarda en
   * el borrador con la identidad del diseño, como hacen los estilos.
   */
  const aplicarCartel = useCallback((preset: PosterPreset) => {
    setSucio(true);
    setPosterPresetTocado(false);
    setOv((o) => ({
      ...o,
      posterPresetId: preset.id,
      poster: { ...(o.poster ?? {}), ...(preset.poster ?? {}) },
    }));
  }, []);

  const alOrden = useCallback((orden: number[]) => patch({ stepOrder: orden }), [patch]);
  const alOcultos = useCallback((ocultos: number[]) => patch({ hiddenStepIds: ocultos }), [patch]);
  const alLocales = useCallback((locales: string[]) => patch({ locales }), [patch]);
  /**
   * Las preguntas que este evento no enseña.
   *
   * Va por el mismo `patch` que los pasos ocultos: se manda a la previa y al
   * borrador a la vez. El paso que se quede sin ninguna visible desaparece por
   * su cuenta —lo decide el runtime— y por eso aquí no se toca `hiddenStepIds`:
   * escribir ahí dejaría el paso oculto incluso después de volver a enseñar una
   * pregunta, y nadie recordaría haberlo pedido.
   */
  const alComponentesOcultos = useCallback(
    (ocultos: number[]) => patch({ hiddenComponentIds: ocultos }),
    [patch],
  );

  if (stationsQ.isLoading || (!!surveyId && deploymentsQ.isLoading)) {
    return <Skeleton className="h-[520px]" />;
  }
  if (!base) {
    /*
     * La encuesta elegida no está desplegada en este evento — estado NORMAL
     * nada más cambiar el selector, no un error. Antes este return devolvía
     * sólo el párrafo, y como el selector vive dentro de la tarjeta, elegir
     * una encuesta sin desplegar hacía desaparecer la pestaña entera, con el
     * propio selector dentro: ni diseño, ni forma de volver ni de desplegar.
     * Se conserva la columna de «Encuestas y enlaces» —selector y «Desplegar
     * en todas», que es justo lo que este estado pide— y el aviso ocupa el
     * hueco del diseño.
     */
    return (
      <Card data-tour="design-journey">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-[var(--color-accent-ink)]" />
            {t('designJourney.cardTitle')}
            <HelpHint section="design-journey" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-5 lg:flex-row">
            {eventId != null ? (
              <div className={`w-full lg:w-[264px] lg:flex-none ${ZONA}`}>
                <XurveyDeploySection
                  eventId={eventId}
                  surveyId={surveyId}
                  onSurveyId={setElegida}
                />
              </div>
            ) : null}
            <p className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-[var(--color-border-default)] p-8 text-center text-sm text-[var(--color-fg-muted)] lg:ml-5">
              {t('designJourney.notDeployed')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const url = xurveyPublicUrl(base.slug, { preview: true });

  const herramientas = (
    <DesignPane
      design={(ov.design ?? {}) as Record<string, unknown>}
      tokens={ov.styleOverrides ?? {}}
      // Lo que trae la encuesta, dicho por la propia previa: es la única que
      // sabe qué tema tiene y cómo queda resuelto.
      heredado={canal.heredado}
      onDesign={alDesign}
      onToken={alToken}
      slug={base.slug}
      poster={(ov.poster ?? {}) as Record<string, unknown>}
      onPoster={alPoster}
      posterPresetId={ov.posterPresetId ?? null}
      posterPresetTocado={posterPresetTocado}
      onAplicarPosterPreset={aplicarCartel}
      prizeLinkUrl={(ov.prizeLinkUrl ?? '') as string}
      prizeLinkLabel={(ov.prizeLinkLabel ?? '') as string}
      prizeLinkStyle={ov.prizeLinkStyle ?? ''}
      encuestaConJuego={encuestaConJuego}
      encuestaConWelcome={encuestaConWelcome}
      welcome={ov.welcome ?? {}}
      onWelcome={alWelcome}
      onPrizeLink={(delta) => {
        /*
         * Va por `patch()` como el resto de overrides: se manda a la previa y
         * al borrador a la vez. No pasa por `onDesign` porque no es diseño —
         * es el destino de un botón, y el runtime lo funde en el `config` del
         * componente de juego, no en la capa de composición.
         */
        patch({
          ...(delta.url !== undefined ? { prizeLinkUrl: delta.url } : {}),
          ...(delta.label !== undefined ? { prizeLinkLabel: delta.label } : {}),
          ...(delta.style !== undefined ? { prizeLinkStyle: delta.style || undefined } : {}),
        });
      }}
      gameCode={game}
      onGameCode={(code) => {
        setGame(code);
        setSucio(true);
        /*
         * Y decírselo a la PREVIA.
         *
         * El juego no vive en `overrides` sino en su propia columna del
         * despliegue (`gameCode`), así que no pasaba por `patch()` y la previa
         * no se enteraba: elegías otro juego y el teléfono seguía con el
         * anterior hasta guardar y recargar. Se manda por el mismo canal que
         * el resto; `''` significa «el de la encuesta» y viaja como `null`
         * para que el runtime sepa que hay que soltar el que tenía.
         */
        canal.enviar({ type: 'xurvey:preview/overrides', gameCode: code || null });
      }}
    />
  );

  return (
    <Card data-tour="design-journey">
      <CardHeader className={`mb-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between ${ZONA}`}>
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-[var(--color-accent-ink)]" />
            {t('designJourney.cardTitle')}
            <HelpHint section="design-journey" />
          </CardTitle>
          <p className="text-sm text-[var(--color-fg-muted)]">
            {t('designJourney.subtitle', { n: String(despliegues.length) })}
          </p>
          {/* Cuánta gente llega al final. Es el número que dice si el diseño
              que se está tocando funciona, así que va con el título y no
              escondido en otra pantalla. */}
          {statsQ.data && statsQ.data.sessions.total > 0 ? (
            <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
              {t('designJourney.completion', {
                ok: String(statsQ.data.sessions.completed),
                total: String(statsQ.data.sessions.total),
                pct: String(statsQ.data.sessions.completionRate),
              })}
              {statsQ.data.medianMs
                ? ` · ${t('designJourney.median', {
                    s: String(Math.round(statsQ.data.medianMs / 1000)),
                  })}`
                : ''}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1" data-tour="dj-save">
          <div className="flex items-center gap-2">
          {surveyId != null && (station?.id != null || enlaceDePrueba) ? (
            <TestQrDialog
              surveyId={surveyId}
              entityId={station?.id != null ? String(station.id) : null}
              testLink={enlaceDePrueba}
              // El QR de prueba nace con el diseño que se está probando.
              onCreated={(link) =>
                xurveyApi.updateDeployment(link.id, {
                  overrides: ov,
                  gameCode: game === '' ? null : game,
                  defaultLocale: defaultLocale === '' ? null : defaultLocale,
                })
              }
            />
          ) : null}
          <Button
            // En pausa sigue habilitado: pulsar es un reintento explícito.
            onClick={() => guardar.mutate({ aMano: true, borrador: { ov, game, defaultLocale } })}
            loading={guardar.isPending}
            disabled={!sucio}
          >
            <Save className="mr-1.5 h-4 w-4" />
            {t('common.save')}
          </Button>
          </div>
          {/*
            Qué está pasando con lo que se acaba de tocar.
            
            Es la mitad del arreglo: un autoguardado que no se ve deja la misma
            duda que no tenerlo —«¿se habrá guardado?»— y lleva a pulsar el
            botón por si acaso.

            En pausa lo dice, y dice por qué: el toast dura cuatro segundos y
            esta línea se queda mientras el borrador sea el rechazado.
          */}
          <span className="text-[10px] text-[var(--color-fg-muted)]" data-testid="dj-save-status">
            {guardar.isPending
              ? t('designJourney.autosaving')
              : enPausa
                ? t('designJourney.autosavePaused')
                : espera
                  ? t('designJourney.autosaveWaiting', {
                      hora: new Date(espera.hasta).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      }),
                    })
                  : sucio
                    ? t('designJourney.autosavePending')
                    : guardadoEn
                      ? t('designJourney.autosaved', {
                          hora: guardadoEn.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          }),
                        })
                      : t('designJourney.autosaveOn')}
          </span>
          {motivoVisible ? (
            <span
              data-testid="dj-save-reason"
              className={cn(
                'max-w-[320px] whitespace-normal break-words text-right text-[10px] leading-snug',
                'text-[var(--color-error)]',
              )}
            >
              {motivoVisible}
            </span>
          ) : null}
          {/*
            Guardar aplica a lo que ya está en marcha; desplegar sólo hace
            falta para las estaciones que aún no tienen esta encuesta. Se dice
            aquí porque, sin decirlo, la única lectura posible es que hay que
            volver a desplegar cada vez para que los cambios cuajen.
          */}
          {sinDesplegar > 0 ? (
            <span className="text-[10px] text-[var(--color-fg-muted)]">
              {t('designJourney.notAllDeployed', { n: String(sinDesplegar) })}
            </span>
          ) : null}
        </div>
      </CardHeader>

      <CardContent>
        {/* Tres zonas y se ven como tres: qué se responde · cómo queda · con
            qué se toca. Sin línea entre ellas, en una pantalla ancha el ojo
            las lee como una sola columna larga y hay que buscar dónde acaba
            cada cosa. En apilado no hay línea vertical que valga: ahí separa
            el propio salto. */}
        {/*
          Tres columnas, y la de la derecha PEGADA AL TELÉFONO.
          
          «Survey and links» y «Screens and control» vivían debajo de los
          pasos, en la columna estrecha de la izquierda. Ahí abajo, en una
          tableta —que es desde donde se usa esto el 90% de las veces— caían
          fuera de la pantalla: para ver dónde está desplegada la encuesta o
          repartir el mando había que bajar, y al bajar se perdía de vista el
          teléfono, que es justo lo que se está juzgando.
          
          `flex-wrap` en el tramo medio y no una maqueta distinta por tamaño:
          en tableta apaisada las tres caben y la referencia queda al lado del
          teléfono; en vertical, la tercera baja sola a una fila propia a todo
          lo ancho, que es más útil que espachurrar tres columnas en 768 px.
          En escritorio no envuelve nunca.
        */}
        <div className="flex flex-col gap-4 md:flex-row md:flex-wrap">
          {/* Izquierda: los pasos y, debajo, qué encuesta y dónde está puesta.
              Juntas porque son la misma pregunta —qué se responde aquí— y
              separadas en dos tarjetas obligaban a mirar arriba y abajo para
              contestarla. */}
          <div
            data-tour="dj-steps"
            /*
             * `scrollbar-gutter: stable` reserva SIEMPRE el canal de la barra
             * en las dos columnas: sin él, el ancho útil cambiaba 15 px al
             * aparecer el scroll y la lista de pasos, con ancho fijo, quedaba
             * recortada por el `overflow-x-hidden` de abajo. La columna pasa
             * de 264 a 280 px para que, canal incluido, el hueco útil sea el
             * mismo que antes. `md:` porque sólo ahí hay scroll propio.
             *
             * `overflow-x-hidden` NO es decorativo.
             *
             * Poner `overflow-y: auto` hace que el navegador calcule
             * `overflow-x` como `auto` también —lo dice la especificación: si
             * uno deja de ser `visible`, el otro tampoco puede serlo—, así que
             * la columna se ganó una barra horizontal en cuanto la estreché.
             * Se corta en el eje que no queremos y se desplaza sólo en el que
             * sí. Y vuelve a 248 px: a 228 el contenido iba apretado, que es lo
             * que sacó la barra a pasear.
             */
            className={`flex w-full flex-col gap-5 overflow-x-hidden md:max-h-[calc(100dvh-200px)] md:w-[280px] md:flex-none md:overflow-y-auto md:[scrollbar-gutter:stable] ${ZONA}`}
          >
            {/*
              Idiomas y paginado, encima de la lista.
              
              Son las dos preguntas que uno se hace MIRANDO los pasos —en qué
              idiomas se responde y cómo se le dice a quien responde por dónde
              va— y estaban repartidas en dos pestañas del panel de
              propiedades. Aquí se contestan sin abrir nada.
            */}
            <JourneyTopControls
              idiomas={idiomasEncuesta}
              locales={ov.locales ?? []}
              onLocales={alLocales}
              defaultLocale={defaultLocale}
              onDefaultLocale={alDefaultLocale}
              paginado={((ov.design ?? {}) as Record<string, unknown>).progressStyle as string ?? ''}
              onPaginado={(v) => alDesign({ progressStyle: v })}
            />

            {/*
              Dos pestañas, pasos por defecto. El consentimiento es del mismo
              asunto —qué recorrido hace la gente— pero apilado bajo la lista
              de pasos empujaba la columna izquierda fuera de la pantalla en una
              tableta; en pestaña se llega en un toque y la lista de pasos
              conserva su alto. Un punto en la pestaña avisa de un bloqueo sin
              tener que abrirla.
            */}
            <Tabs defaultValue="steps">
              <TabsList className="h-10">
                <TabsTrigger value="steps" className="h-8 flex-1">
                  {t('designJourney.tab.steps')}
                </TabsTrigger>
                <TabsTrigger value="consent" className="h-8 flex-1">
                  <span className="inline-flex items-center gap-1.5">
                    {t('designJourney.tab.consent')}
                    {consentQ.data?.issues.some((i) => i.severity === 'block') ? (
                      <span
                        aria-label={t('designJourney.consent.blockedTitle')}
                        className="inline-block h-2 w-2 rounded-full bg-[var(--color-error)]"
                      />
                    ) : null}
                  </span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="steps" className="mt-3">
                <StepsPane
                  pasos={pasosConCandado}
                  ocultos={ov.hiddenStepIds ?? []}
                  componentesOcultos={ov.hiddenComponentIds ?? []}
                  orden={ov.stepOrder ?? []}
                  seleccionado={pasoSel}
                  abandonos={abandonos}
                  onSeleccionar={setPasoSel}
                  onOrden={alOrden}
                  onOcultos={alOcultos}
                  onComponentesOcultos={alComponentesOcultos}
                />
              </TabsContent>

              <TabsContent value="consent" className="mt-3">
                {base ? (
                  <ConsentCard
                    config={consentQ.data}
                    loading={consentQ.isLoading}
                    consent={ov.consent}
                    onConsent={(delta) => patch({ consent: { ...(ov.consent ?? {}), ...delta } })}
                    esXotski={esXotski}
                  />
                ) : null}
              </TabsContent>
            </Tabs>
          </div>

          {/*
            La previa se queda FIJA al desplazar.
            
            Medido en una tableta apaisada (1024×768): por encima del teléfono
            hay 575 px de cromo de página —migas, cabecera del evento,
            pestañas, cabecera de la tarjeta— y quedan 193 px libres. No hay
            tamaño de teléfono que quepa ahí: encogerlo hasta caber lo dejaría
            en un sello donde no se distingue lo que se está juzgando.
            
            Así que en vez de pelear por que todo quepa, lo que no se pierde es
            la previa: al bajar a por las herramientas o a por los enlaces, el
            teléfono se queda pegado arriba. `self-start` es obligatorio — un
            hijo de flex se estira a todo el alto por defecto y entonces
            `sticky` no tiene margen donde pegarse.
          */}
          <section
            data-tour="dj-preview"
            className={`flex min-w-[240px] flex-1 justify-center md:sticky md:top-4 md:self-start ${ZONA}`}
          >
            <PhoneFrame titulo={t('designJourney.previewTitle')}>
              <iframe
                ref={canal.frame}
                key={base.slug}
                src={url}
                title={t('designJourney.previewTitle')}
                className="block h-full w-full border-0 bg-white"
                /* Sin barra propia: el marco ya tiene la altura de un teléfono
                   y lo que no cabe se desplaza DENTRO de la encuesta, como en
                   un móvil de verdad. Una barra gris pegada al bisel delata
                   que esto es un iframe y estropea justo lo que se está
                   juzgando. */
                scrolling="no"
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            </PhoneFrame>
          </section>

          {/*
            La referencia: dónde está puesta la encuesta y qué se llevan al
            recinto. Y, cuando no hay carril donde meterlas —tableta—, también
            las herramientas: antes que una cuarta columna, van aquí debajo.
          */}
          <div
            data-tour="dj-reference"
            /*
             * Se desplaza POR DENTRO, no empujando la página.
             *
             * Sin el tope, esta columna crecía con lo que tuviera —y en tableta
             * carga además las herramientas— hasta dejar 2.000 px de recorrido
             * de página: para tocar un color había que bajar, y al bajar el
             * teléfono se iba de la pantalla. Con el alto acotado al del
             * teléfono, la fila entera cabe y lo que sobra se desplaza aquí
             * dentro, con la previa siempre a la vista.
             */
            className={`flex w-full flex-col gap-5 overflow-x-hidden md:max-h-[calc(100dvh-200px)] md:min-w-[288px] md:flex-1 md:overflow-y-auto md:[scrollbar-gutter:stable] lg:max-w-[376px] ${ZONA}`}
          >
        <div>
          {/*
            Desplegar es de UN evento: pone la encuesta en las estaciones de
            ese evento concreto. Desde el proyecto no se ofrece —no habría
            a cuál— y en su lugar se dice cuántos despliegues alcanza lo que
            se está diseñando, que es la pregunta que uno se hace ahí.
          */}
          {eventId != null ? (
            <XurveyDeploySection
              eventId={eventId}
              surveyId={surveyId}
              onSurveyId={setElegida}
            />
          ) : (
            <p className="text-[11px] leading-relaxed text-[var(--color-fg-muted)]">
              {/*
                Singular y plural de verdad, en las dos cifras. `t()` sólo
                interpola, así que «los 1 evento(s)» se escribe solo — y ése
                es el detalle que hace que una pantalla parezca sin
                terminar.

                Y los eventos son los que REALMENTE alcanza, no los del
                proyecto: una encuesta puede estar desplegada sólo en uno de
                los tres, y decir «los 3 eventos» sería prometer que se
                cambia algo que no se toca.
              */}
              {t('designJourney.projectScope', {
                deployments: t(
                  despliegues.length === 1
                    ? 'designJourney.oneDeployment'
                    : 'designJourney.nDeployments',
                  { n: despliegues.length },
                ),
                events: t(
                  eventosAlcanzados === 1
                    ? 'designJourney.oneEvent'
                    : 'designJourney.nEvents',
                  { n: eventosAlcanzados },
                ),
              })}
            </p>
          )}
        </div>

        {/*
          Los estilos guardados, JUSTO DEBAJO de la encuesta: es de donde se
          parte. Estaban en el carril, encima de las pestañas de diseño, lejos
          del selector — y el aplicado se olvidaba al salir. Ahora se lee de
          arriba abajo: qué encuesta, con qué estilo, y el despliegue recuerda
          cuál.
        */}
        <div data-tour="dj-presets" className="rounded-lg border border-[var(--color-border-default)]">
          <StylePresetBar
            apariencia={apariencia}
            seleccionado={ov.stylePresetId ?? null}
            modificado={presetTocado}
            onAplicar={aplicarEstilo}
          />
        </div>

        {/*
          Lo que se llevan al recinto: la pantalla y el mando.
          
          Sólo en un evento. Desde el proyecto no habría a qué encender —el
          mando enciende LAS ESTACIONES DE UN EVENTO— y ofrecerlo ahí sería
          repartir una URL que apaga cosas de sitios distintos a la vez.
        */}
        {eventId != null ? (
          <div data-tour="dj-screens">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
              {t('designJourney.screens')}
            </p>
            <ScreenControlCard
              // El nombre que verá el agente en su mando: el de la encuesta que
              // se está respondiendo, que es como la gente del sitio la llama.
              nombreEvento={encuesta?.name ?? base.name ?? 'Evento'}
              linkIds={despliegues.map((d) => d.id)}
            />
          </div>
        ) : null}

            {/*
              El plan B es para TABLETA, no para el carril plegado.
              
              La condición era `belowDesktop || !slot`, y al plegar el carril el
              hueco del portal deja de existir (`slot` pasa a null): las
              propiedades se colaban aquí, en mitad del contenido. Plegar es
              pedir MÁS sitio para el lienzo, así que meterle ahí el panel que
              se acaba de guardar hace justo lo contrario de lo que se pidió.
              
              Ahora sólo baja aquí cuando de verdad no hay carril donde vivir
              —por debajo de `lg` el carril es una hoja a pantalla completa, no
              una columna—. Plegado, las propiedades no se enseñan y se
              recuperan con el chevrón del borde, que sigue ahí porque el
              Journey mantiene el carril reclamado.
            */}
            {belowDesktop ? (
              <div className="rounded-lg border border-[var(--color-border-default)]">
                {herramientas}
              </div>
            ) : null}
          </div>

          {!belowDesktop && slot
            ? createPortal(<div className="h-full">{herramientas}</div>, slot)
            : null}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * El teléfono.
 *
 * Con bisel propio y no con un color del tema: el marco tiene que separarse de
 * la tarjeta en claro Y en oscuro, y cualquier token de superficie se confunde
 * con uno de los dos. Un teléfono es negro; pintarlo de negro no es saltarse
 * los tokens, es dibujar un objeto.
 *
 * La proporción es la de un móvil real (390×844) y **importa**: una encuesta
 * que cabe en un recuadro achatado puede no caber en un teléfono, y entonces
 * el editor estaría aprobando algo que nadie verá así.
 *
 * El aviso de vista previa va FUERA, encima. Dentro le robaba altura a la
 * encuesta —llegó a cortar el botón de continuar— y ensuciaba justo lo que se
 * está mirando. El runtime sólo lo pinta él cuando nadie lo embebe.
 */
function PhoneFrame({ children, titulo }: { children: React.ReactNode; titulo: string }) {
  return (
    <div
      className="flex w-full flex-col items-center gap-2"
      style={{
        /*
         * El ancho SALE DEL ALTO QUE HAY. La proporción del aparato es fija
         * (390×844), así que un teléfono de 372 px pide 805 px de alto — más de
         * lo que tiene una tableta apaisada, y por eso el editor obligaba a
         * bajar para ver el botón de continuar de la encuesta que estabas
         * juzgando.
         *
         * Se invierte la cuenta: del alto disponible se deduce el ancho que
         * cabe. `100dvh` y no `vh` porque en tableta la barra del navegador se
         * recoge al desplazar y `vh` se queda con el alto de antes.
         *
         * Los 260 px son el resto de la página —cabecera, pestañas, cabecera de
         * la tarjeta, el rótulo de arriba—; el suelo de 230 px evita que en una
         * ventana muy baja se quede en un sello ilegible: ahí es mejor
         * desplazar un poco que no distinguir nada.
         */
        maxWidth: 'min(372px, max(230px, calc((100dvh - 260px) * 390 / 844)))',
      }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
        {titulo}
      </p>

      <div
        role="img"
        aria-label={titulo}
        className="relative w-full rounded-[42px] p-[10px] shadow-2xl"
        style={{
          // Bisel oscuro con un filo claro: el filo es lo que lo despega del
          // fondo cuando la tarjeta también es oscura.
          background: 'linear-gradient(#2a2d36, #14161c)',
          boxShadow: '0 0 0 1px rgba(255,255,255,.14), 0 18px 40px rgba(0,0,0,.45)',
        }}
      >
        {/* Los botones del aparato. Son decoración y por eso van ocultos a
            quien navega con lector: anunciar «botón de volumen» en algo que no
            se puede pulsar es ruido. */}
        {/* Silencio, volumen y encendido. En porcentaje y no en píxeles: el
            marco se estrecha con la ventana y en píxeles acabarían en mitad de
            la pantalla en cuanto la tarjeta se hace pequeña. */}
        <Boton lado="izq" arriba="14%" alto="3.2%" />
        <Boton lado="izq" arriba="20%" alto="6%" />
        <Boton lado="izq" arriba="27.5%" alto="6%" />
        <Boton lado="der" arriba="22%" alto="9%" />

        {/* La isla, sobre la pantalla, que es donde está en el aparato. */}
        <span
          aria-hidden
          className="absolute left-1/2 top-[18px] z-10 h-[22px] w-[92px] -translate-x-1/2 rounded-full"
          style={{ background: '#0b0c10' }}
        />
        <div
          className="overflow-hidden rounded-[32px] bg-white"
          style={{ aspectRatio: '390 / 844' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Un botón del lateral. Puro dibujo.
 *
 * Sobresale dos píxeles y lleva el mismo degradado que el bisel, invertido:
 * así se lee como la misma pieza de metal y no como una mancha pegada al
 * borde. Con el gris claro anterior parecían cuatro etiquetas sueltas.
 */
const Boton = ({
  lado,
  arriba,
  alto,
}: {
  lado: 'izq' | 'der';
  arriba: string;
  alto: string;
}) => (
  <span
    aria-hidden
    className="absolute w-[4px]"
    style={{
      top: arriba,
      height: alto,
      [lado === 'izq' ? 'left' : 'right']: -2,
      background: 'linear-gradient(#14161c, #2a2d36)',
      borderRadius: lado === 'izq' ? '3px 0 0 3px' : '0 3px 3px 0',
    }}
  />
);